import { isTrustedHttpsUrl } from '@/utils/url/get-safe-redirect-url'

/**
 * Build the Shopify Admin embedded URL that loads our app at
 * `/shopify/install-finalize`. The user lands inside Shopify Admin's
 * iframe where App Bridge is available, so we can mint a fresh
 * 1-minute session token to authenticate the claim.
 *
 * Format:
 *   https://admin.shopify.com/store/{store_handle}/apps/{app_handle}/shopify/install-finalize
 *
 * `app_handle` is the Shopify Partner Dashboard slug; we expose it as
 * NEXT_PUBLIC_SHOPIFY_APP_HANDLE so this URL can be built client-side
 * (the API has the same value as SHOPIFY_APP_HANDLE).
 *
 * Returns null if the env var is missing — the caller should fall back
 * to a non-recovery path (logging an error) since we can't construct a
 * valid URL without it.
 */
export function buildShopifyEmbeddedFinalizeUrl(shopDomain: string): string | null {
    const appHandle = (process.env.NEXT_PUBLIC_SHOPIFY_APP_HANDLE ?? '').trim()
    if (!appHandle) return null
    const storeHandle = shopDomain.replace(/\.myshopify\.com$/i, '')
    if (!storeHandle) return null
    return `https://admin.shopify.com/store/${storeHandle}/apps/${appHandle}/shopify/install-finalize`
}

export function buildShopifyEmbeddedInstallUrl(shopDomain: string): string | null {
    const appHandle = (process.env.NEXT_PUBLIC_SHOPIFY_APP_HANDLE ?? '').trim()
    if (!appHandle) return null
    const storeHandle = shopDomain.replace(/\.myshopify\.com$/i, '')
    if (!storeHandle) return null
    return `https://admin.shopify.com/store/${storeHandle}/apps/${appHandle}/shopify/install`
}

/** Shopify resolves the signed-in store when the initiating product does not know it yet. */
export function buildShopifyAdminInstallUrl(shopDomain?: string | null): string | null {
    if (shopDomain && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shopDomain))
        return buildShopifyEmbeddedInstallUrl(shopDomain)

    const appHandle = (process.env.NEXT_PUBLIC_SHOPIFY_APP_HANDLE ?? '').trim()

    return appHandle ? `https://admin.shopify.com/apps/${encodeURIComponent(appHandle)}/shopify/install` : null
}

/**
 * Entry URL for connecting Shopify to a brand: the public App Store listing,
 * where Shopify asks which store to install on (or opens the app on the chosen
 * store when it is already installed) and supplies the `shop` itself. Shopify's
 * terms forbid asking the merchant to type their shop domain, and a workspace
 * can run a different store per brand, so the merchant — not the workspace —
 * picks the store.
 *
 * The App Store listing URL is configured explicitly via
 * `NEXT_PUBLIC_SHOPIFY_APP_STORE_URL` (the full listing URL, e.g.
 * `https://apps.shopify.com/<listing-handle>`). It is NOT derivable from
 * `NEXT_PUBLIC_SHOPIFY_APP_HANDLE` — the App Store *listing* handle is a
 * separate value set when the listing is created and differs from the
 * Partner Dashboard *app* handle (the app handle is for admin deep-links /
 * managed pricing, not the public listing). Unlisted/dev apps have no
 * listing, so this is unset there.
 *
 * Returns null when no listing URL is configured; the caller surfaces an
 * error rather than navigating to a broken link.
 */
export function buildShopifyAppStoreUrl(): string | null {
    const appStoreUrl = (process.env.NEXT_PUBLIC_SHOPIFY_APP_STORE_URL ?? '').trim()
    return appStoreUrl || null
}

/**
 * Managed-pricing URLs are always Shopify-hosted
 * (`admin.shopify.com/store/{handle}/charges/{app}/pricing_plans`). Reject
 * anything else so a `billingUrl` query/localStorage value can't be abused
 * as an open redirect.
 */
export function sanitizeShopifyBillingUrl(url: string | null | undefined) {
    if (!url) return null
    return isTrustedHttpsUrl(url, ['admin.shopify.com']) ? url : null
}
